/**
 * Service for Cumulative Premium V2 - Portal backend API
 *
 * Connects to the new option_stream Django app endpoints.
 */

import { apiClient } from './api';
import { qfRestGet, qfSubscribe } from './qfClient';
import { normalizeOptionsTicker, underlyingPriceTicker } from '../utils/tickerDisplay';

// Portal API wrapper response format
interface APIResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
  status: string;
}

// Types matching the Portal backend response
export interface PremiumSnapshot {
  ticker: string;
  call_bought: number;
  call_sold: number;
  net_call_premium: number;
  put_bought: number;
  put_sold: number;
  net_put_premium: number;
  premium_pressure: number;
  spot_price: number | null;
  next_expiration_only: boolean;
  timestamp: string;
}

export interface PremiumSnapshotAll {
  total_call_bought: number;
  total_call_sold: number;
  total_net_call_premium: number;
  total_put_bought: number;
  total_put_sold: number;
  total_net_put_premium: number;
  total_premium_pressure: number;
  ticker_count: number;
  next_expiration_only: boolean;
  timestamp: string;
}

export interface PremiumTimeseriesPoint {
  bucket: string;
  cum_call_bought: number;
  cum_call_sold: number;
  cum_net_call_premium: number;
  cum_put_bought: number;
  cum_put_sold: number;
  cum_net_put_premium: number;
  cum_premium_pressure: number;
  spot_price: number | null;
}

// QF REST /v1/steamroom/premium/timeseries response shape (frozen four-code book)
interface QfPremiumPoint {
  minute: string;
  cum_call_bought: number;
  cum_call_sold: number; // already negative
  cum_put_bought: number;
  cum_put_sold: number; // already negative
  cum_pressure: number;
}

interface QfPremiumResponse {
  symbol: string;
  date: string;
  exp_filter: string;
  resolved_expiration: string | null;
  points: QfPremiumPoint[];
}

/**
 * Parse a QF bucket/minute string to a Date on a stable UTC axis.
 *
 * QF puts two timestamp shapes on the same chart: bare ISO from REST history
 * (`2026-06-09T13:30`, implicitly UTC) and Z-suffixed ISO from the live profile
 * snapshot (`2026-06-09T13:30:00Z`). `new Date(bareString)` parses as the
 * browser's LOCAL time while the Z form parses as UTC, so feeding both raw to
 * the chart splits the series by the local UTC offset. Normalize a bare string
 * to UTC first (append `Z` when no timezone is present), matching
 * gexBetaService.toEpochMs so both widgets share one axis.
 *
 * @param bucket ISO timestamp, with or without a timezone designator.
 * @returns A Date anchored to the correct instant regardless of input shape.
 */
export function bucketToDate(bucket: string): Date {
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(bucket) ? bucket : `${bucket}Z`;
  return new Date(iso);
}

export function mapQfPoint(p: QfPremiumPoint): PremiumTimeseriesPoint {
  return {
    bucket: p.minute,
    cum_call_bought: p.cum_call_bought,
    cum_call_sold: p.cum_call_sold,
    cum_net_call_premium: p.cum_call_bought + p.cum_call_sold,
    cum_put_bought: p.cum_put_bought,
    cum_put_sold: p.cum_put_sold,
    cum_net_put_premium: p.cum_put_bought + p.cum_put_sold,
    cum_premium_pressure: p.cum_pressure,
    spot_price: null, // price/candle overlay is the Phase-2 OHLC path, not net-sweep
  };
}

/** Sum N already-cumulative series onto a unified, sorted bucket axis with carry-forward. */
export function combineCumulativeSeries(
  perTicker: PremiumTimeseriesPoint[][],
): PremiumTimeseriesPoint[] {
  const buckets = Array.from(
    new Set(perTicker.flatMap((s) => s.map((p) => p.bucket))),
  ).sort();
  const cursors = perTicker.map(() => 0);
  const lasts: (PremiumTimeseriesPoint | null)[] = perTicker.map(() => null);
  const out: PremiumTimeseriesPoint[] = [];
  for (const bucket of buckets) {
    const acc = {
      cum_call_bought: 0,
      cum_call_sold: 0,
      cum_net_call_premium: 0,
      cum_put_bought: 0,
      cum_put_sold: 0,
      cum_net_put_premium: 0,
      cum_premium_pressure: 0,
    };
    perTicker.forEach((series, i) => {
      while (cursors[i] < series.length && series[cursors[i]].bucket <= bucket) {
        lasts[i] = series[cursors[i]];
        cursors[i] += 1;
      }
      const v = lasts[i];
      if (v) {
        acc.cum_call_bought += v.cum_call_bought;
        acc.cum_call_sold += v.cum_call_sold;
        acc.cum_net_call_premium += v.cum_net_call_premium;
        acc.cum_put_bought += v.cum_put_bought;
        acc.cum_put_sold += v.cum_put_sold;
        acc.cum_net_put_premium += v.cum_net_put_premium;
        acc.cum_premium_pressure += v.cum_premium_pressure;
      }
    });
    out.push({ bucket, ...acc, spot_price: null });
  }
  return out;
}

// Transformed response for frontend use
export interface PremiumTimeseriesResponse {
  ticker: string;
  next_expiration_only: boolean;
  data: PremiumTimeseriesPoint[];
}

// OHLC types
export interface OHLCPoint {
  bucket: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
}

export interface OHLCTimeseriesResponse {
  ticker: string;
  underlying: string;
  is_index: boolean;
  data: OHLCPoint[];
}

export interface AvailableOHLCTickersResponse {
  index: string[];
  stock: string[];
}

export interface TickerMappingsResponse {
  option_to_underlying: Record<string, string>;
  underlying_to_options: Record<string, string[]>;
  index_tickers: string[];
}

export const premiumV2Service = {
  /**
   * Get snapshot for a single ticker
   */
  async getSnapshot(
    ticker: string,
    nextExpirationOnly = false
  ): Promise<PremiumSnapshot> {
    const params: Record<string, string> = {};
    if (nextExpirationOnly) {
      params.next_expiration = 'true';
    }
    const response = await apiClient.get<APIResponse<PremiumSnapshot>>(
      `/api/option-stream/premium/snapshot/${ticker.toLowerCase()}/`,
      params
    );
    return response.data;
  },

  /**
   * Get snapshots for multiple tickers
   */
  async getSnapshotMultiple(
    tickers: string[],
    nextExpirationOnly = false
  ): Promise<PremiumSnapshot[]> {
    const params: Record<string, string> = {
      tickers: tickers.join(','),
    };
    if (nextExpirationOnly) {
      params.next_expiration = 'true';
    }
    const response = await apiClient.get<APIResponse<PremiumSnapshot[]>>(
      '/api/option-stream/premium/snapshot/',
      params
    );
    return response.data;
  },

  /**
   * Get combined snapshot across all tickers
   */
  async getSnapshotAll(nextExpirationOnly = false): Promise<PremiumSnapshotAll> {
    const params: Record<string, string> = {};
    if (nextExpirationOnly) {
      params.next_expiration = 'true';
    }
    const response = await apiClient.get<APIResponse<PremiumSnapshotAll>>(
      '/api/option-stream/premium/snapshot/all/',
      params
    );
    return response.data;
  },

  /**
   * Get cumulative timeseries for tickers (for chart loading)
   */
  async getTimeseries(
    tickers: string[],
    nextExpirationOnly = false,
    date?: string
  ): Promise<PremiumTimeseriesResponse[]> {
    const expFilter = nextExpirationOnly ? 'nextexp' : 'allexp';
    const perTicker: PremiumTimeseriesPoint[][] = [];
    for (const ticker of tickers) {
      const params: Record<string, string> = {
        symbol: ticker,
        exp_filter: expFilter,
        // Frozen four-code book (auto/slan/isoi/slai). Product FZN Premium
        // moved off this set 2026-09-20 onto /v1/premium/timeseries. Do not
        // point this widget back at the QF product route.
        include_complex: 'false',
      };
      if (date) {
        params.date = date;
      }
      const qf = await qfRestGet<QfPremiumResponse>(
        '/v1/steamroom/premium/timeseries',
        params
      );
      perTicker.push(qf.points.map(mapQfPoint));
    }
    return [
      {
        ticker: tickers[0] ?? '',
        next_expiration_only: nextExpirationOnly,
        data: combineCumulativeSeries(perTicker),
      },
    ];
  },

  // ============================================================================
  // OHLC Endpoints
  // ============================================================================

  /**
   * Get OHLC timeseries for a ticker (1-minute bars)
   */
  async getOHLCTimeseries(ticker: string, date?: string): Promise<OHLCTimeseriesResponse> {
    // OHLC is price data: key off the underlying INDEX ticker, never the options
    // root. QF returns underlying candles for SPX, not SPXW, so the SPX family
    // (settings.ticker = SPXW) would otherwise come back empty.
    const root = underlyingPriceTicker(ticker.toUpperCase());
    const params: Record<string, string> = { symbol: root };
    if (date) params.date = date;
    const qf = await qfRestGet<{
      symbol: string;
      date: string;
      candles: {
        minute: string;
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number | null;
        trade_count: number;
      }[];
    }>('/v1/ohlc', params);
    return {
      ticker: qf.symbol,
      underlying: qf.symbol,
      is_index: false,
      data: qf.candles.map((c) => ({
        bucket: c.minute,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume ?? null,
      })),
    };
  },

  /**
   * Get latest OHLC bar for a ticker
   */
  async getLatestOHLC(
    ticker: string
  ): Promise<OHLCPoint & { ticker: string; underlying: string; is_index: boolean }> {
    const response = await apiClient.get<
      APIResponse<OHLCPoint & { ticker: string; underlying: string; is_index: boolean }>
    >(`/api/option-stream/ohlc/latest/${ticker.toUpperCase()}/`);
    return response.data;
  },

  // ============================================================================
  // Tickers Endpoints
  // ============================================================================

  /**
   * Get available OHLC tickers by type
   */
  async getAvailableOHLCTickers(): Promise<AvailableOHLCTickersResponse> {
    const response = await apiClient.get<APIResponse<AvailableOHLCTickersResponse>>(
      '/api/option-stream/tickers/available-ohlc/'
    );
    return response.data;
  },

  /**
   * Get ticker mappings (option roots to underlying)
   */
  async getTickerMappings(): Promise<TickerMappingsResponse> {
    const response = await apiClient.get<APIResponse<TickerMappingsResponse>>(
      '/api/option-stream/tickers/mappings/'
    );
    return response.data;
  },
};

// ============================================================================
// Live net-sweep via QF profile:{T} Centrifugo snapshot
// ============================================================================

type Split = { call_bought: number; call_sold: number; put_bought: number; put_sold: number };

interface ProfileSnapshot {
  ticker: string;
  as_of: string;
  session_day: string;
  spot: string | null;
  premium_cum?: Record<string, Split>;
  premium_cum_single_leg?: Record<string, Split>;
}

/** Soonest expiration >= sessionDay (nextexp), or null to include all (allexp). */
export function resolveExpiration(
  cum: Record<string, Split>,
  nextExpirationOnly: boolean,
  sessionDay: string,
): string | null {
  if (!nextExpirationOnly) return null;
  const future = Object.keys(cum).filter((e) => e >= sessionDay).sort();
  return future.length ? future[0] : null;
}

/**
 * Sum a single snapshot's premium_cum into signed cumulative totals.
 *
 * Returns null when the book carries no data — absent field, empty map, or a
 * nextexp filter that matches no expiration. A zero-initialised sum over an
 * empty book is indistinguishable from a genuinely flat book, and splicing that
 * fabricated zero onto a REST-seeded series is what snaps the chart to the
 * axis. Callers must treat null as "no live data yet" and leave the series
 * alone; a loaded book that really nets flat still returns real zeros.
 *
 * @param cum premium_cum / premium_cum_single_leg keyed by expiration, if present.
 * @param nextExpirationOnly Restrict to the soonest expiration >= sessionDay.
 * @param sessionDay QF session day (YYYY-MM-DD) used to resolve nextexp.
 * @returns Signed cumulative totals, or null when there is nothing to sum.
 */
export function snapshotToTotals(
  cum: Record<string, Split> | null | undefined,
  nextExpirationOnly: boolean,
  sessionDay: string,
): Split | null {
  if (!cum) return null;
  const entries = Object.entries(cum);
  if (entries.length === 0) return null;

  const onlyExp = resolveExpiration(cum, nextExpirationOnly, sessionDay);
  if (nextExpirationOnly && onlyExp === null) return null;

  const acc: Split = { call_bought: 0, call_sold: 0, put_bought: 0, put_sold: 0 };
  for (const [exp, s] of entries) {
    if (onlyExp !== null && exp !== onlyExp) continue;
    acc.call_bought += s.call_bought;
    acc.call_sold += s.call_sold;
    acc.put_bought += s.put_bought;
    acc.put_sold += s.put_sold;
  }
  return acc;
}

export function totalsToPoint(t: Split, bucket: string, spot: number | null): PremiumTimeseriesPoint {
  const cum_call_sold = -t.call_sold; // sold stored as positive magnitude in premium_cum
  const cum_put_sold = -t.put_sold;
  const cum_net_call = t.call_bought + cum_call_sold;
  const cum_net_put = t.put_bought + cum_put_sold;
  return {
    bucket,
    cum_call_bought: t.call_bought,
    cum_call_sold,
    cum_net_call_premium: cum_net_call,
    cum_put_bought: t.put_bought,
    cum_put_sold,
    cum_net_put_premium: cum_net_put,
    cum_premium_pressure: cum_net_call - cum_net_put,
    spot_price: spot,
  };
}

/** Gross notional magnitude used to detect empty/cold live snapshots. */
export function premiumMagnitude(p: Pick<
  PremiumTimeseriesPoint,
  'cum_call_bought' | 'cum_call_sold' | 'cum_put_bought' | 'cum_put_sold'
>): number {
  return (
    Math.abs(p.cum_call_bought) +
    Math.abs(p.cum_call_sold) +
    Math.abs(p.cum_put_bought) +
    Math.abs(p.cum_put_sold)
  );
}

/**
 * Fraction of the established tail below which a live cumulative point is
 * treated as a still-loading book rather than a real reading.
 *
 * Both series are session-CUMULATIVE, so the live tip can only ever grow away
 * from the REST tail — a legitimate update is >= the tail, never a fraction of
 * it. Observed live (2026-07-14): NVDA's live book reported 344,684 against a
 * REST tail of 440,701,131 (0.08%) while QF was still replaying the tape. An
 * absolute floor cannot separate that from a quiet tape; a ratio can.
 */
const LIVE_PREMIUM_MIN_RATIO = 0.5;

/**
 * Guard a live profile point before it is spliced onto the REST-seeded series.
 *
 * QF's profile book can lag the REST session total by orders of magnitude while
 * a symbol is still loading. Because both series are cumulative, a live point
 * that is a small fraction of the tail is never a real reading — it is a
 * partially-rebuilt book, and accepting it collapses the chart to the axis.
 *
 * @param last Current tail of the series (REST seed or last trusted live point).
 * @param next Incoming live point.
 * @returns true when `next` may replace/extend the series.
 */
export function shouldAcceptLivePremium(
  last: PremiumTimeseriesPoint | null | undefined,
  next: PremiumTimeseriesPoint,
): boolean {
  if (!last) return true;
  const lastMag = premiumMagnitude(last);
  const nextMag = premiumMagnitude(next);
  // No established tail to protect (pre-open / first minutes): accept.
  if (lastMag <= 10_000) return true;
  return nextMag >= lastMag * LIVE_PREMIUM_MIN_RATIO;
}

/** Minute key YYYY-MM-DDTHH:MM for REST vs live bucket comparison. */
export function premiumMinuteKey(bucket: string): string {
  return bucketToDate(bucket).toISOString().slice(0, 16);
}

/**
 * Merge a REST history seed with any live points already applied in the
 * client. Full-replace of the ref with REST alone wiped the live tip and
 * re-introduced multi-minute lag whenever React Query refetched (focus,
 * reconnect, staleTime).
 *
 * - REST is authoritative for completed history minutes.
 * - Live points strictly after the last REST minute are kept.
 * - For the last REST minute, prefer live if it passes the cold-snap guard.
 */
export function mergePremiumHistory(
  rest: PremiumTimeseriesPoint[],
  live: PremiumTimeseriesPoint[],
): PremiumTimeseriesPoint[] {
  if (!rest.length) return live.slice();
  if (!live.length) return rest.slice();

  const lastRest = rest[rest.length - 1];
  const lastRestKey = premiumMinuteKey(lastRest.bucket);

  let liveTip: PremiumTimeseriesPoint | null = null;
  const liveAfter: PremiumTimeseriesPoint[] = [];
  for (const p of live) {
    const k = premiumMinuteKey(p.bucket);
    if (k < lastRestKey) continue;
    if (k === lastRestKey) {
      liveTip = p;
      continue;
    }
    liveAfter.push(p);
  }

  const out = rest.slice(0, -1);
  if (liveTip && shouldAcceptLivePremium(lastRest, liveTip)) {
    out.push(liveTip);
  } else {
    out.push(lastRest);
  }
  out.push(...liveAfter);
  return out;
}

/**
 * Subscribe to live cumulative-premium for a ticker.
 * Subscribes exactly ONE normalized root (no cross-root fan-out or summation);
 * disambiguation (e.g. SPX->SPXW) is handled by normalizeOptionsTicker. Each
 * profile snapshot is converted to one PremiumTimeseriesPoint. Returns an
 * unsubscribe function.
 *
 * Callers should pass the current series tail into shouldAcceptLivePremium
 * before splicing, so partially-loaded books cannot snap the chart to zero.
 *
 * @param onPoint Receives a live point only when the snapshot carries a real book.
 * @param onSpot Receives spot when the premium book is absent, so the price
 *   overlay keeps ticking for roots QF publishes no single-leg book for.
 */
export function subscribePremiumLive(
  ticker: string,
  nextExpirationOnly: boolean,
  onPoint: (p: PremiumTimeseriesPoint) => void,
  onSpot?: (spot: number | null, asOf: string) => void,
): () => void {
  const root = normalizeOptionsTicker(ticker.toUpperCase());
  return qfSubscribe(`profile:${root}`, (data: unknown) => {
    const snap = data as ProfileSnapshot;
    // Net Sweeper history is the four-code book; the live tail must splice
    // premium_cum_single_leg to match. premium_cum is all-trades and would
    // jump the line. Do not read premium_cum_fzn here — that is product FZN.
    //
    // QF omits premium_cum_single_leg entirely for some roots (SPY/QQQ/SPXW as
    // of 2026-07-14). Emit no point rather than a fabricated all-zero one —
    // the widget still gets spot from onSpot below.
    const totals = snapshotToTotals(
      snap.premium_cum_single_leg,
      nextExpirationOnly,
      snap.session_day,
    );
    const spot = snap.spot != null ? parseFloat(snap.spot) : null;
    if (totals === null) {
      onSpot?.(spot, snap.as_of);
      return;
    }
    onPoint(totalsToPoint(totals, snap.as_of, spot));
  });
}
